/**
 * RS-6 — the review sheet: the paper on the left, the decision on the right.
 *
 * Opening a submittal at the desk shows the document in the app's own markup
 * tool (the same one the firm marks drawings up in, so a reviewer learns
 * nothing new), and beside it what the party said, what the firm has said, and
 * the four stamps. Choosing one and sending it prints the stamp onto a copy of
 * every document on the row — that copy is what the party opens.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DrawingMarkupEditor from '@/components/DrawingMarkupEditor';
import { StampBox, STAMP_PRINT } from './StampBox';
import './review-sheet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const abs = (url) => (String(url || '').startsWith('/api/')
  ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

export const STAMP_ORDER = ['approved', 'approved_as_noted', 'resubmit', 'rejected'];

const shortDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US',
    { month: 'short', day: 'numeric', year: 'numeric' });
};

// RS-11a — the same file twice in a stored row is still one page to read.
const dedupeDocs = (docs) => { const seen = new Set(); return docs.filter((d) => { const k = d.url || d.attachment_id || d.name; if (seen.has(k)) return false; seen.add(k); return true; }); };

const issued = (r) => r?.direction === 'issued';
// RS-7b's chain for a drawing the firm issued, in order; `at` is where the row is now.
const CHAIN = [
  { key: 'issued', label: (r) => `Issued to ${r.party_name || r.submitted_by || 'the party'}`, when: (r) => r.issued_at || r.submitted_at },
  { key: 'confirmed', label: (r) => `${r.party_name || r.submitted_by || 'They'} confirm "can build as drawn"`, when: (r) => r.confirmed_at },
  { key: 'accepted', label: () => 'You accept for fabrication', when: (r) => r.accepted_at },
  { key: 'stamped', label: () => 'Stamped copy filed', when: (r) => r.review_date },
  { key: 'released', label: () => 'Released to the client', when: (r) => r.released_to_client_at },
];
const chainAt = (r) => {
  const s = String(r?.status || '');
  if (r?.released_to_client_at) return 5;
  if (r?.is_stamped) return 4;
  if (s === 'accepted_by_both') return 3;
  if (s === 'confirmed') return 2;
  return 1; // issued / reissued / returned
};

export default function SubmittalReviewSheet({ projectId, submittal, canStamp = false,
  stampers = [], paper = {}, earlier = null, canRelease = false, releasers = [],
  clientName = 'the client', onReissue, onChanged, onClose, onStamped }) {
  const [row, setRow] = useState(submittal);
  const [docIndex, setDocIndex] = useState(0);
  const [marks, setMarks] = useState([]);
  // The editor takes its marks at mount, so it is not drawn until they are in.
  const [loaded, setLoaded] = useState(false);
  const [stamp, setStamp] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  // RS-7 — the client is never shown anything by accident, so a release is
  // asked for twice.
  const [asking, setAsking] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const documents = dedupeDocs(row?.documents || []);
  const document = documents[docIndex] || null;
  // The markups and stamped doors address the STORED index, not the shown one.
  const docIndexStored = Math.max(0, (row?.documents || []).indexOf(document));
  const base = `${API}/projects/${projectId}/submittals/${row?.id}`;

  const loadMarks = useCallback(async () => {
    if (!row?.id || !documents.length) { setMarks([]); setLoaded(true); return; }
    setLoaded(false);
    try {
      const r = await axios.get(`${base}/documents/${docIndexStored}/markups`);
      setMarks(r.data?.markups || []);
    } catch { setMarks([]); }
    setLoaded(true);
  }, [base, docIndexStored, row?.id, documents.length]);

  useEffect(() => { loadMarks(); }, [loadMarks]);

  const saveMarks = async (posted) => {
    try {
      const r = await axios.post(`${base}/documents/${docIndexStored}/markups`,
        { markups: posted });
      setMarks(r.data?.markups || []);
      return r.data?.markups || null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Those marks did not save');
      return null;
    }
  };

  const send = async () => {
    if (!stamp) { toast.error('Choose one of the four stamps'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${base}/stamp`, { stamp, note: note.trim() });
      setRow(r.data?.submittal || row);
      toast.success(`${row.submittal_number} stamped ${STAMP_PRINT[stamp]}`);
      if (onStamped) onStamped(r.data?.submittal);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That stamp did not go on');
    }
    setSending(false);
  };

  // RS-7b — the firm's half of the handshake on paper it issued itself.
  const accept = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${base}/accept`, { note: note.trim() });
      setRow(r.data?.submittal || row);
      toast.success(`${row.submittal_number} accepted for fabrication`);
      if (onChanged) onChanged(r.data?.submittal);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not go on');
    }
    setSending(false);
  };

  const release = async () => {
    setReleasing(true);
    try {
      const r = await axios.post(`${base}/release-to-client`, { confirm: true });
      setRow(r.data?.submittal || row);
      setAsking(false);
      toast.success(`${row.submittal_number} is in the Drawing Room`);
      if (onStamped) onStamped(r.data?.submittal);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'That did not go to the client');
    }
    setReleasing(false);
  };

  // RS-7 / RS-11a — one release block, rendered by whichever face is showing.
  const releaseBlock = !row ? null : row.released_to_client_at ? (
    <p className="text-sm kmr-dim" data-testid="submittal-released">
      {`Released to the client · ${shortDate(row.released_to_client_at)}`}
    </p>
  ) : row.can_release ? (
    canRelease ? (
      asking ? (
        <div className="space-y-2" data-testid="submittal-release-confirm">
          <p className="text-sm kmr-read">{`Release the stamped copy to ${clientName}?`}</p>
          <div className="flex gap-2">
            <button type="button" onClick={release} disabled={releasing}
              data-testid="submittal-release-yes"
              className="kmr-go px-3 py-2 rounded text-sm disabled:opacity-60">
              {releasing ? 'Releasing…' : 'Release'}
            </button>
            <button type="button" onClick={() => setAsking(false)}
              data-testid="submittal-release-no"
              className="kmr-pick px-3 py-2 rounded border text-sm">
              Not yet
            </button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAsking(true)}
          data-testid="submittal-release"
          className="kmr-out w-full py-2 rounded border text-sm">
          Release to the client
        </button>
      )
    ) : (
      <p className="text-sm kmr-dim" data-testid="submittal-no-release-perm">
        {`Ask ${releasers.length ? releasers.join(', ') : 'the principal designer'} to release`}
      </p>
    )
  ) : null;

  const stamped = Boolean(row?.is_stamped) || row?.status === 'accepted_by_both';
  // RS-11a — a Drawing Control sheet is type "drawing" with no ".pdf" in its
  // name; the url is what says what it is.
  const kind = /\.pdf(\?|$)/i.test(String(document?.url || ''))
    || String(document?.type || document?.name || '').toLowerCase().includes('pdf') ? 'pdf' : 'image';

  return (
    <div className="kmr-sheet fixed inset-0 z-50 overflow-auto" data-testid="submittal-review-sheet">
      <div className="kmr-rule flex items-center justify-between px-5 py-3 border-b">
        <div className="min-w-0">
          <span className="kmr-link font-mono text-sm">{row?.submittal_number}</span>
          <span className="kmr-read ml-3">{row?.title}</span>
          <span className="text-xs kmr-dim ml-3">{`rev ${row?.revision_number || 1}`}</span>
        </div>
        <button type="button" onClick={onClose} data-testid="submittal-review-close"
          className="kmr-pick text-sm px-3 py-1 border rounded">
          Close
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-0">
        <div className="kmr-rule border-r min-w-0" data-testid="submittal-review-paper">
          {document && loaded ? (
            <DrawingMarkupEditor
              key={`${row?.id}-${docIndex}`}
              embedded
              imageUrl={abs(document.url)}
              fileKind={kind}
              existingMarkups={marks}
              authorKind="firm"
              readOnly={stamped || (issued(row) && chainAt(row) < 2)}
              drawingTitle={document.name || row?.title}
              drawingNumber={row?.submittal_number || ''}
              projectName={row?.party_name || ''}
              versionNumber={row?.revision_number || 1}
              onClose={onClose}
              onSave={saveMarks}
              onSaveMerged={saveMarks}
              onStatus={null}
              onReply={null}
              onDeleteMark={(markId) => axios.delete(
                `${base}/documents/${docIndexStored}/markups/${markId}`)}
            />
          ) : document ? (
            <p className="p-6 text-sm kmr-dim" data-testid="submittal-review-loading">
              Opening the paper…
            </p>
          ) : (
            <p className="p-6 text-sm kmr-dim" data-testid="submittal-review-nodoc">
              Nothing was attached to this hand-in.
            </p>
          )}
        </div>

        <aside className="p-5 space-y-5" data-testid="submittal-review-column">
          <div className="space-y-1 text-sm">
            <p className="text-xs uppercase tracking-wider kmr-dim">For</p>
            <p className="kmr-read" data-testid="submittal-review-for">
              {[row?.kind_label, row?.for_label].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>

          {documents.length > 1 && (
            <div>
              <p className="text-xs uppercase tracking-wider kmr-dim mb-2">Pages</p>
              <div className="flex flex-wrap gap-2">
                {documents.map((d, i) => (
                  <button
                    key={d.attachment_id || d.name || i}
                    type="button"
                    onClick={() => setDocIndex(i)}
                    data-testid={`submittal-doc-${i}`}
                    className={`kmr-pick text-xs px-2 py-1 rounded border${i === docIndex ? ' kmr-pick--on' : ''}`}
                  >
                    {d.name || `File ${i + 1}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {earlier && (
            <div className="text-sm" data-testid="submittal-review-earlier">
              <p className="text-xs uppercase tracking-wider kmr-dim mb-1">Earlier revision</p>
              <p className="kmr-dim">
                {`${earlier.submittal_number} · rev ${earlier.revision_number} · ${earlier.status_label}`}
              </p>
              {earlier.review_notes && (
                <p className="kmr-dim text-xs mt-1">{earlier.review_notes}</p>
              )}
            </div>
          )}

          <div data-testid="submittal-review-thread">
            <p className="text-xs uppercase tracking-wider kmr-dim mb-2">The thread</p>
            <div className="space-y-2">
              <div className="kmr-card p-3 rounded">
                <p className="text-xs kmr-dim">
                  {issued(row)
                    ? `${paper.firm || 'The firm'} · ${shortDate(row?.issued_at || row?.submitted_at)}`
                    : `${row?.party_name || row?.submitted_by || 'The party'} · ${shortDate(row?.submitted_at)}`}
                </p>
                <p className="text-sm kmr-read mt-1">
                  {issued(row)
                    ? `Issued for fabrication · ${(row?.documents || [])[0]?.name || ''}`
                    : (row?.description || 'Handed in with no note.')}
                </p>
                {issued(row) && row?.description ? (
                  <p className="text-sm kmr-read mt-1" data-testid="submittal-issued-note">{row.description}</p>
                ) : null}
              </div>
              {row?.review_notes && (
                <div className="kmr-card p-3 rounded" data-testid="submittal-review-firmnote">
                  <p className="text-xs kmr-dim">
                    {`${row?.reviewer_name || 'The firm'} · rev ${row?.revision_number} · ${shortDate(row?.review_date)}`}
                  </p>
                  <p className="text-sm kmr-read mt-1">{row.review_notes}</p>
                </div>
              )}
            </div>
          </div>

          {row?.party_note && (
            <div className="kmr-card p-3 rounded" data-testid="submittal-party-note">
              <p className="text-xs kmr-dim">{`${row.party_name || 'The party'} sent it back`}</p>
              <p className="text-sm kmr-read mt-1">{row.party_note}</p>
              {(row.party_documents || []).map((d, i) => (
                <a key={`pd-${i}`} href={abs(d.url)} target="_blank" rel="noreferrer"
                  data-testid={`submittal-party-doc-${i}`}
                  className="kmr-link block text-sm mt-1">
                  {d.name || `File ${i + 1}`}
                </a>
              ))}
            </div>
          )}

          {row?.can_reissue && onReissue ? (
            <button type="button" onClick={() => onReissue(row)}
              data-testid="submittal-reissue"
              className="kmr-out w-full py-2 rounded border text-sm">
              {`Reissue rev ${(row.revision_number || 1) + 1}`}
            </button>
          ) : null}

          {!issued(row) && row?.can_accept ? (
            canStamp ? (
              <div className="space-y-2">
                <textarea rows={2} value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                  placeholder="A note for the party, printed with the stamp"
                  data-testid="submittal-accept-note"
                  className="kmr-field w-full text-sm border rounded p-2" />
                <button type="button" onClick={accept} disabled={sending}
                  data-testid="submittal-accept"
                  className="kmr-go w-full py-2 rounded text-sm disabled:opacity-60">
                  {sending ? 'Accepting…' : 'Accept for fabrication'}
                </button>
              </div>
            ) : (
              <p className="text-sm kmr-dim" data-testid="submittal-no-stamp-perm">
                {`Waiting for ${stampers.length ? stampers.join(', ') : 'the principal designer'} to stamp`}
              </p>
            )
          ) : null}

          {issued(row) ? (
            <div className="space-y-3" data-testid="submittal-issued-face">
              <p className="text-xs uppercase tracking-wider kmr-dim">Where it stands</p>
              <ol className="kmr-chain" data-testid="submittal-chain">
                {CHAIN.map((step, i) => {
                  const at = chainAt(row);
                  const state = i + 1 < at ? 'done' : i + 1 === at ? 'now' : 'next';
                  const when = step.when(row);
                  return (
                    <li key={step.key} data-s={state} data-testid={`submittal-chain-${step.key}`}>
                      <i /><span className="kmr-read">{step.label(row)}</span>
                      <span className="kmr-dim text-xs">{state === 'now' && !when ? 'waiting' : shortDate(when)}</span>
                    </li>
                  );
                })}
              </ol>
              {row.status === 'returned' && row.party_note ? (
                <div className="kmr-card p-3 rounded" data-testid="submittal-returned-note">
                  <p className="text-xs kmr-dim">{`${row.party_name || 'They'} said it needs a change`}</p>
                  <p className="text-sm kmr-read mt-1">{row.party_note}</p>
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                {row.status === 'confirmed' && !row.is_stamped ? (
                  canStamp ? (
                    <button type="button" onClick={accept} disabled={sending} data-testid="submittal-accept"
                      className="kmr-go px-3 py-2 rounded text-sm disabled:opacity-60">
                      {sending ? 'Accepting…' : 'Accept for fabrication'}
                    </button>
                  ) : (
                    <p className="text-sm kmr-dim" data-testid="submittal-no-accept-perm">
                      {`Waiting for ${stampers.length ? stampers.join(', ') : 'the principal designer'} to accept`}
                    </p>
                  )
                ) : null}
                {row.status === 'returned' ? (
                  <span className="text-sm kmr-dim" data-testid="submittal-reissue-hint">{`Issue rev ${(row.revision_number || 1) + 1} from the register`}</span>
                ) : null}
              </div>
              {row.is_stamped || row.status === 'accepted_by_both' ? (
                <>
                  <StampBox stamp={row.stamp} firm={paper.firm || ''} initials={row.reviewer_initials || ''}
                    date={String(row.review_date || '').slice(0, 10)} wording={paper.wording || ''}
                    third={row.confirmed_by ? `${row.confirmed_by.name} · confirmed ${String(row.confirmed_at || '').slice(0, 10)}` : ''}
                    label={row.status === 'accepted_by_both' ? 'ACCEPTED FOR FABRICATION' : ''}
                    testId="submittal-stamp-print" />
                  {(row.documents || []).map((d, i) => (d.stamped_url ? (
                    <a key={`stamped-${i}`} href={abs(d.stamped_url)} target="_blank" rel="noreferrer"
                      data-testid={`submittal-open-stamped-${i}`} className="kmr-link block text-sm hover:underline">
                      {`Open the stamped copy · ${d.name || `File ${i + 1}`}`}
                    </a>
                  ) : null))}
                </>
              ) : null}
              {releaseBlock}
            </div>
          ) : stamped ? (
            <div className="space-y-3" data-testid="submittal-stamped">
              <p className="text-xs uppercase tracking-wider kmr-dim">On the paper</p>
              <StampBox
                stamp={row.stamp}
                firm={paper.firm || ''}
                initials={row.reviewer_initials || ''}
                date={String(row.review_date || '').slice(0, 10)}
                wording={paper.wording || ''}
                third={row.status === 'accepted_by_both' && row.confirmed_by
                  ? `${row.confirmed_by.name} · confirmed ${String(row.confirmed_at || '').slice(0, 10)}`
                  : ''}
                label={row.status === 'accepted_by_both' ? 'ACCEPTED FOR FABRICATION' : ''}
                testId="submittal-stamp-print"
              />
              {(row.documents || []).map((d, i) => (d.stamped_url ? (
                <a key={`stamped-${i}`} href={abs(d.stamped_url)} target="_blank" rel="noreferrer"
                  data-testid={`submittal-open-stamped-${i}`}
                  className="kmr-link block text-sm hover:underline">
                  {`Open the stamped copy · ${d.name || `File ${i + 1}`}`}
                </a>
              ) : null))}

              {releaseBlock}
            </div>
          ) : canStamp ? (
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-wider kmr-dim">The stamp</p>
              <div className="grid grid-cols-2 gap-2">
                {STAMP_ORDER.map((key) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setStamp(key)}
                    data-testid={`submittal-stamp-${key}`}
                    aria-pressed={stamp === key}
                    className="kmr-pick px-2 py-3 rounded border tracking-wider uppercase"
                    style={{ fontSize: 'var(--km-t-code)' }}
                  >
                    {STAMP_PRINT[key]}
                  </button>
                ))}
              </div>
              <textarea
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 1000))}
                placeholder="A note for the party, printed with the stamp"
                data-testid="submittal-stamp-note"
                className="kmr-field w-full text-sm border rounded p-2"
              />
              <button
                type="button"
                onClick={send}
                disabled={sending}
                data-testid="submittal-stamp-send"
                className="kmr-go w-full py-2 rounded text-sm disabled:opacity-60"
              >
                {sending ? 'Stamping…' : 'Stamp and send'}
              </button>
            </div>
          ) : (
            <p className="text-sm kmr-dim" data-testid="submittal-no-stamp-perm">
              {`Waiting for ${stampers.length ? stampers.join(', ') : 'the principal designer'} to stamp`}
            </p>
          )}
        </aside>
      </div>
    </div>
  );
}
